import { Injectable, signal } from '@angular/core';

import { AskRynAction } from './ask-ryn.service';
import { AskRynGridRegistry } from '../grid-commands/ask-ryn-grid-registry.service';

/**
 * Shared open/close state for the Ask Ryn chat panel. Decoupled from the host so
 * any trigger can open the chat: the docked launcher button, the docked Ryn/wren
 * avatar (in-app-help), or a future "ask about this page" deep link. The host
 * component renders the panel when `isOpen()` is true.
 *
 * Also the one-way hand-off point for a reply's structured action: AskRynComponent
 * publishes it via setPendingAction, and the in-app-help wren (which already
 * imports this service) reads pendingAction() in an effect and calls consumeAction()
 * once handled. Keeping the action here — not a switch inside AskRynComponent that
 * imports the wren — avoids a circular ask-ryn ↔ in-app-help lib dependency.
 *
 * US 35406: a `gridCommands` action never reaches the wren. It goes to
 * AskRynGridRegistry, which changes the grid on the current page, and pageHelp()
 * adds that grid's catalog to what the panel sends.
 */
@Injectable({ providedIn: 'root' })
export class AskRynUiService {
  readonly isOpen = signal(false);

  /**
   * Whether Ask Ryn is available to the signed-in user. Set by AskRynHostComponent
   * from the app's security profile (fail-closed: starts false until resolved).
   * The docked wren launcher reads this so it only appears for permitted users,
   * and open() is a no-op while false. Apps that don't gate Ask Ryn flip this to
   * true unconditionally on init.
   */
  readonly canUse = signal(false);

  /** Latest unconsumed structured action from a Ryn reply, or null. */
  readonly pendingAction = signal<AskRynAction | null>(null);

  /** Why the last grid reply changed nothing, shown in the panel. */
  readonly gridNotice;

  // A parameter with a default (not inject()) so `new AskRynUiService()` in specs still works.
  constructor(
    private readonly grids: AskRynGridRegistry = new AskRynGridRegistry()
  ) {
    this.gridNotice = this.grids.notice;
  }

  /**
   * The page help to send, plus the catalog of any grid registered on the page.
   * `newestReplyAt` is the newest Ryn reply's server timestamp at send time.
   */
  pageHelp(
    pageHelpContent: string,
    conversationId?: string,
    newestReplyAt?: string | Date | null
  ): string {
    return this.grids.withGridContext(
      pageHelpContent,
      conversationId ?? null,
      newestReplyAt ?? null
    );
  }

  open(): void {
    if (!this.canUse()) {
      return; // gated off for this user — never open the chat
    }
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  toggle(): void {
    this.isOpen.update((v) => !v);
  }

  /**
   * Publish a reply's action for the wren to act on (fly / start walkthrough).
   * `replyAt` is the reply's server timestamp; a gridCommands reply is applied only
   * when it is newer than the send.
   */
  setPendingAction(
    action: AskRynAction,
    conversationId?: string,
    replyAt?: string | Date | null
  ): void {
    if (action.kind === 'gridCommands') {
      this.grids.handle(action, conversationId ?? null, replyAt ?? null);
      return;
    }
    this.pendingAction.set(action);
  }

  /** Clear the pending action once the consumer (wren) has handled it. */
  consumeAction(): void {
    this.pendingAction.set(null);
  }
}
